import { useCallback, useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { Save, History, RotateCcw, Eye, Code2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, GoldButton, GhostButton } from "@/admin/ui";

const TABS = [
  { key: "css", label: "CSS", lang: "css" },
  { key: "js", label: "JavaScript", lang: "javascript" },
  { key: "html", label: "HTML", lang: "html" },
];

export const CodeEditor = () => {
  const [code, setCode] = useState({ html: "", css: "", js: "" });
  const [tab, setTab] = useState("css");
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autosave, setAutosave] = useState(true);
  const timer = useRef(null);
  const dirty = useRef(false);

  const loadVersions = useCallback(() => api.get("/admin/code/versions").then(({ data }) => setVersions(data)), []);

  useEffect(() => {
    api.get("/admin/code").then(({ data }) => setCode({ html: data.html || "", css: data.css || "", js: data.js || "" }));
    loadVersions();
  }, [loadVersions]);

  const doSave = useCallback(async (label) => {
    setSaving(true);
    try {
      await api.put("/admin/code", { ...code, label });
      dirty.current = false;
      await loadVersions();
      if (label !== "Auto-save") toast.success("Código salvo");
    } catch { toast.error("Erro ao salvar"); } finally { setSaving(false); }
  }, [code, loadVersions]);

  // autosave debounce
  useEffect(() => {
    if (!autosave || !dirty.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => doSave("Auto-save"), 3000);
    return () => clearTimeout(timer.current);
  }, [code, autosave, doSave]);

  const onChange = (val) => {
    dirty.current = true;
    setCode((c) => ({ ...c, [tab]: val ?? "" }));
  };

  const restore = async (v) => {
    if (!window.confirm(`Restaurar versão de ${new Date(v.saved_at).toLocaleString("pt-BR")}?`)) return;
    const { data } = await api.post(`/admin/code/restore/${v.id}`);
    setCode({ html: data.html, css: data.css, js: data.js });
    toast.success("Versão restaurada");
    setShowVersions(false);
  };

  const srcDoc = `<!doctype html><html><head><style>body{background:#120D0D;color:#fff;font-family:sans-serif;padding:16px;} ${code.css}</style></head><body>${code.html}<script>try{${code.js}}catch(e){document.body.innerHTML+='<pre style=color:#ff5>'+e+'</pre>'}<\/script></body></html>`;

  return (
    <div data-testid="admin-code">
      <PageTitle title="Editor de Código" subtitle="CSS / JS / HTML personalizados injetados no site"
        action={
          <div className="flex gap-2">
            <GhostButton onClick={() => setShowVersions((s) => !s)} data-testid="code-versions-btn"><History className="w-4 h-4 inline mr-1" /> Versões</GhostButton>
            <GoldButton onClick={() => doSave("Manual")} disabled={saving} data-testid="code-save"><Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}</GoldButton>
          </div>
        } />

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="!p-0 overflow-hidden">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-3">
            <div className="flex">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-3 text-sm font-display transition-colors ${tab === t.key ? "text-[#F5C542] border-b-2 border-[#F5C542]" : "text-white/50 hover:text-white"}`}
                  data-testid={`code-tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-white/50 pr-2 cursor-pointer">
              <input type="checkbox" checked={autosave} onChange={(e) => setAutosave(e.target.checked)} /> Auto-save
            </label>
          </div>
          <Editor
            height="480px"
            theme="vs-dark"
            language={TABS.find((t) => t.key === tab).lang}
            value={code[tab]}
            onChange={onChange}
            options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, automaticLayout: true, wordWrap: "on" }}
          />
        </Card>

        <Card className="!p-0 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3 text-sm text-white/60">
            <Eye className="w-4 h-4 text-[#F5C542]" /> Preview em tempo real
          </div>
          <iframe title="preview" srcDoc={srcDoc} className="w-full flex-1 bg-[#120D0D]" style={{ minHeight: 480 }} sandbox="allow-scripts" data-testid="code-preview" />
        </Card>
      </div>

      {showVersions && (
        <Card className="mt-4">
          <h3 className="font-display font-bold text-white mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#F5C542]" /> Histórico de Versões</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <div>
                  <span className="text-sm text-white">{v.label}</span>
                  <span className="text-xs text-white/40 ml-2">{new Date(v.saved_at).toLocaleString("pt-BR")}</span>
                </div>
                <button onClick={() => restore(v)} className="flex items-center gap-1 text-xs text-white/70 hover:text-[#F5C542]"><RotateCcw className="w-3.5 h-3.5" /> Restaurar</button>
              </div>
            ))}
            {versions.length === 0 && <p className="text-white/40 text-sm">Nenhuma versão salva.</p>}
          </div>
        </Card>
      )}
    </div>
  );
};
